<!-- 名前入力・生まれ年選択スライド -->
<script setup lang="ts">
import { computed } from 'vue';

import type Inquiry from '@/javascripts/utils/inquiry';
import { getBirthYears } from '@/javascripts/utils/inquiry_form_options';

import type { InquiryValidation } from '@/javascripts/composables/inquiry/validation';

/** プロパティ */
const props = defineProps<{
  /** 問い合わせオブジェクト */
  lastName: Inquiry['lastName'],
  firstName: Inquiry['firstName'],
  lastNameKana: Inquiry['lastNameKana'],
  firstNameKana: Inquiry['firstNameKana'],
  birthYearCd: Inquiry['birthYearCd'],
  /** 問い合わせバリデーション */
  v$: InquiryValidation
}>();

/** カスタムイベント */
const emit = defineEmits([
  'back', 
  'next', 
  'update:last-name', 
  'update:first-name', 
  'update:last-name-kana', 
  'update:first-name-kana', 
  'update:birth-year-cd'
]);

/** フォーム項目の computed プロパティ */
const localLastName = computed({
  get: () => props.lastName,
  set: (value) => emit('update:last-name', value)
});
const localFirstName = computed({
  get: () => props.firstName,
  set: (value) => emit('update:first-name', value)
});
const localLastNameKana = computed({
  get: () => props.lastNameKana,
  set: (value) => emit('update:last-name-kana', value)
});
const localFirstNameKana = computed({
  get: () => props.firstNameKana,
  set: (value) => emit('update:first-name-kana', value)
});
const localBirthYearCd = computed({
  get: () => props.birthYearCd,
  set: (value) => emit('update:birth-year-cd', value)
});

/** 生まれ年の選択肢リスト */
const birthYears = getBirthYears();

/** お名前のバリデーションエラー(姓優先) */
const nameValidationErrors = computed(
  () => props.v$.lastName.$error ? props.v$.lastName.$errors : props.v$.firstName.$errors
);

/** ふりがなのバリデーションメッセージ(姓優先) */
const kanaValidationErrors = computed(
  () => props.v$.lastNameKana.$error ? props.v$.lastNameKana.$errors : props.v$.firstNameKana.$errors
);

/** お名前・ふりがな・生まれ年のバリデーション結果 */
const invalid = computed(() =>
  props.v$.lastName.$invalid || props.v$.firstName.$invalid ||
  props.v$.lastNameKana.$invalid || props.v$.firstNameKana.$invalid ||
  props.v$.birthYearCd.$invalid
);
</script>

<template>
  <div class="slide_body">
    <div class="option_wrap">
      <h3 class="option_title required">
        お名前と生まれ年を教えて下さい
      </h3>
      <div class="option_group mb16">
        <div class="option_input_flex mb8">
          <div class="label">お名前</div>
          <div class="input_wrap">
            <input
              id="last_name"
              v-model="localLastName"
              type="text"
              name="last_name"
              placeholder="姓"
              required
            >
            <input
              id="first_name"
              v-model="localFirstName"
              type="text"
              name="first_name"
              placeholder="名"
              required
            >
          </div>
        </div>
        <div
          v-for="(error, key) in nameValidationErrors"
          :key="key"
          class="validation_message_box"
        >{{ error.$message }}</div>                  
        <div class="option_input_flex mb8">
          <div class="label">ふりがな</div>
          <div class="input_wrap">
            <input
              id="last_name_kana"
              v-model="localLastNameKana"
              type="text"
              name="last_name_kana"
              placeholder="せい"
              required
            >
            <input
              id="first_name_kana"
              v-model="localFirstNameKana"
              type="text"
              name="first_name_kana"
              placeholder="めい"
              required
            >
          </div>
        </div>
        <div
          v-for="(error, key) in kanaValidationErrors"
          :key="key"
          class="validation_message_box"
        >{{ error.$message }}</div>        
        <div class="option_input_flex">
          <div class="label">生まれ年</div>
          <div class="input_wrap">
            <select
              id="birth_year"
              v-model="localBirthYearCd"
              required
            >
              <option :value="null" disabled>選択してください</option>
              <option
                v-for="birthYear in birthYears"
                :key="birthYear.cd"
                :value="birthYear.cd"
              >{{ birthYear.text }}</option>
            </select>
          </div>
        </div>
        <div
          v-for="(error, key) in v$.birthYearCd.$errors"
          :key="key"
          class="validation_message_box"
        >{{ error.$message }}</div>                
      </div>
      
      <div class="option_annotation_box">
        ※個人情報は公開されません
      </div>
    </div>
    <div class="slide_control_group">
      <a
        id="ph_back_3"
        class="back_link"
        @click="emit('back')"
      >&lt; 戻る</a>
      <button
        id="ph_next_4"
        :disabled="invalid"
        class="form_button"
        @click="emit('next')"
      >
        次へ
      </button>
    </div>
  </div>
</template>